/**
 * Beyond Sure — AH3: Batched single-shot intake.
 *
 * The default OnboardingFlow asks ~16-18 questions ONE PER SCREEN (AH0.3 forensics). This is the
 * batched alternative: the high-value customer-profile questions grouped into a few sections on ONE
 * scrollable screen with a single submit. It writes to the SAME persistence (localStorage
 * `bsure_onboarding_v1`, the OnboardingAnswers shape) so the rest of the app — and the server's
 * get_user_intake_context injection — reads it unchanged. Additive: rendered only when OnboardingFlow
 * is mounted with `batched`; the sequential flow is byte-for-byte unaffected when off.
 *
 * Palette obeys the 4-colour law: Deep Green (#0b3d2e), Black CTA, White cards, cream paper.
 */
import { useMemo, useState } from "react";
import type { OnboardingAnswers } from "./OnboardingFlow";

const STORAGE_KEY = "bsure_onboarding_v1";

type Existing = "health" | "term" | "motor" | "none";

interface FormState {
  name: string;
  age: string;
  pincode: string;
  spouse: boolean;
  children: string;
  parents: string;
  employment: string;
  income: string;
  budget: string;
  goal: string;
  existing: Existing[];
}

const EMPLOYMENT: [string, string][] = [
  ["salaried", "Salaried"], ["self_employed", "Self-employed"],
  ["business", "Business owner"], ["other", "Other"],
];
const INCOME: [string, string][] = [
  ["lt5", "Under ₹5L / yr"], ["5to10", "₹5–10L"], ["10to20", "₹10–20L"],
  ["20to50", "₹20–50L"], ["gt50", "₹50L+"],
];
const BUDGET: [string, string][] = [
  ["lt1k", "Under ₹1k / mo"], ["1to3k", "₹1–3k"], ["3to6k", "₹3–6k"], ["gt6k", "₹6k+"],
];
const GOAL: [string, string][] = [
  ["protect_family", "Protect my family"], ["health_cover", "Health cover"],
  ["save_tax", "Save tax"], ["wealth", "Grow wealth"], ["retire", "Retirement"],
];
const EXISTING_OPTS: [Existing, string][] = [
  ["health", "Health insurance"], ["term", "Term life"], ["motor", "Motor"], ["none", "None yet"],
];

const labelS: React.CSSProperties = { display: "block", fontSize: 13, fontWeight: 600, color: "#0b3d2e", marginBottom: 6 };
const fieldS: React.CSSProperties = { width: "100%", padding: "10px 12px", borderRadius: 10, border: "1px solid rgba(11,61,46,0.18)", background: "#fff", color: "#1a1a1a", fontSize: 14 };
const sectionS: React.CSSProperties = { background: "rgba(255,255,255,0.72)", backdropFilter: "blur(8px)", border: "1px solid rgba(11,61,46,0.10)", borderRadius: 16, padding: 20, marginBottom: 16 };
const h2S: React.CSSProperties = { fontSize: 15, fontWeight: 700, color: "#0b3d2e", marginBottom: 14 };

export function BatchedIntakeForm({ onComplete }: { onComplete: (a: OnboardingAnswers) => void }) {
  const stored = useMemo<Record<string, unknown>>(() => {
    try { return JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
  }, []);
  const dep = (stored.dependents as Record<string, unknown>) || {};
  const [f, setF] = useState<FormState>({
    name: String(stored.name || ""),
    age: String(stored.age || ""),
    pincode: String(stored.pincode || ""),
    spouse: Boolean(dep.spouse),
    children: dep.children != null ? String(dep.children) : "",
    parents: dep.parents != null ? String(dep.parents) : "",
    employment: String(stored.employment || ""),
    income: String(stored.income || ""),
    budget: String(stored.budget || ""),
    goal: String(stored.goal || ""),
    existing: Array.isArray(stored.existing) ? (stored.existing as Existing[]) : [],
  });
  const [err, setErr] = useState<string | null>(null);

  const toggleExisting = (e: Existing) =>
    setF((p) => {
      if (e === "none") return { ...p, existing: p.existing.includes("none") ? [] : ["none"] };
      const without = p.existing.filter((x) => x !== "none");
      return { ...p, existing: without.includes(e) ? without.filter((x) => x !== e) : [...without, e] };
    });

  function submit(ev: React.FormEvent) {
    ev.preventDefault();
    setErr(null);
    if (!f.name.trim()) { setErr("Please enter your name."); return; }
    const ageN = parseInt(f.age, 10);
    if (!ageN || ageN < 18 || ageN > 100) { setErr("Please enter a valid age (18–100)."); return; }
    const merged = {
      ...stored,
      name: f.name.trim(),
      age: f.age,
      pincode: f.pincode.trim(),
      dependents: { self: true, spouse: f.spouse, children: parseInt(f.children, 10) || 0, parents: parseInt(f.parents, 10) || 0 },
      employment: f.employment || null,
      income: f.income || null,
      budget: f.budget || null,
      goal: f.goal || null,
      existing: f.existing,
      onboarding_done: true,
    };
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch { /* quota — proceed */ }
    onComplete(merged as unknown as OnboardingAnswers);
  }

  const selects: [string, keyof FormState, [string, string][]][] = [
    ["Employment", "employment", EMPLOYMENT],
    ["Annual income", "income", INCOME],
    ["Monthly budget", "budget", BUDGET],
    ["Primary goal", "goal", GOAL],
  ];

  return (
    <div style={{ minHeight: "100dvh", background: "var(--color-paper, #faf9f7)", padding: "32px 16px", display: "flex", justifyContent: "center" }}>
      <form onSubmit={submit} style={{ width: "100%", maxWidth: 560 }} aria-label="Quick profile intake">
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#0b3d2e", marginBottom: 4 }}>Your profile — all at once</h1>
        <p style={{ color: "#475569", fontSize: 14, marginBottom: 20 }}>One short form instead of question-by-question. You can edit any of it later.</p>

        <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
          <section style={sectionS}>
            <h2 style={h2S}>Basics</h2>
            <div style={{ marginBottom: 14 }}>
              <label style={labelS} htmlFor="bi-name">Full name</label>
              <input id="bi-name" style={fieldS} value={f.name} onChange={(e) => setF((p) => ({ ...p, name: e.target.value }))} placeholder="Your name" />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div>
                <label style={labelS} htmlFor="bi-age">Age</label>
                <input id="bi-age" style={fieldS} inputMode="numeric" value={f.age} onChange={(e) => setF((p) => ({ ...p, age: e.target.value.replace(/\D/g, "") }))} placeholder="e.g. 34" />
              </div>
              <div>
                <label style={labelS} htmlFor="bi-pin">PIN code / city</label>
                <input id="bi-pin" style={fieldS} value={f.pincode} onChange={(e) => setF((p) => ({ ...p, pincode: e.target.value }))} placeholder="e.g. 560001" />
              </div>
            </div>
          </section>

          <section style={sectionS}>
            <h2 style={h2S}>Household</h2>
            <label style={{ ...labelS, display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={f.spouse} onChange={(e) => setF((p) => ({ ...p, spouse: e.target.checked }))} /> I have a spouse to cover
            </label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <div>
                <label style={labelS} htmlFor="bi-children">Children</label>
                <input id="bi-children" style={fieldS} inputMode="numeric" value={f.children} onChange={(e) => setF((p) => ({ ...p, children: e.target.value.replace(/\D/g, "") }))} placeholder="0" />
              </div>
              <div>
                <label style={labelS} htmlFor="bi-parents">Dependent parents</label>
                <input id="bi-parents" style={fieldS} inputMode="numeric" value={f.parents} onChange={(e) => setF((p) => ({ ...p, parents: e.target.value.replace(/\D/g, "") }))} placeholder="0" />
              </div>
            </div>
          </section>

          <section style={sectionS}>
            <h2 style={h2S}>Money &amp; goals</h2>
            {selects.map(([lab, key, opts]) => (
              <div key={key} style={{ marginBottom: 14 }}>
                <label style={labelS} htmlFor={`bi-${key}`}>{lab}</label>
                <select id={`bi-${key}`} style={fieldS} value={String(f[key])} onChange={(e) => setF((p) => ({ ...p, [key]: e.target.value }))}>
                  <option value="">Select…</option>
                  {opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
                </select>
              </div>
            ))}
            <label style={labelS}>Existing cover</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {EXISTING_OPTS.map(([v, t]) => {
                const on = f.existing.includes(v);
                return (
                  <button type="button" key={v} onClick={() => toggleExisting(v)} aria-pressed={on}
                    style={{ padding: "8px 14px", borderRadius: 999, fontSize: 13, cursor: "pointer",
                      border: on ? "1px solid #0b3d2e" : "1px solid rgba(11,61,46,0.18)",
                      background: on ? "#0b3d2e" : "#fff", color: on ? "#fff" : "#1a1a1a" }}>
                    {t}
                  </button>
                );
              })}
            </div>
          </section>
        </fieldset>

        {err && <p role="alert" style={{ color: "#b91c1c", fontSize: 13, marginBottom: 12 }}>{err}</p>}
        <button type="submit" style={{ width: "100%", padding: "14px", borderRadius: 12, border: 0, background: "#000", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
          Save my profile
        </button>
      </form>
    </div>
  );
}

export default BatchedIntakeForm;
